// File: src/pages/reports/GSTInputOutputReports.jsx
// ✅ UI = SAME STYLE AS "Assign Sales Person Pincodes" page (p-card / p-table-wrap / clean table)
// ✅ Functionality = SAME AS YOUR CURRENT GSTInputOutputReports (INPUT+OUTPUT merge, month/year filter, search, viewMode, export PDF/XLSX, pagination)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Card, Button, Input, Spinner } from "reactstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import axios from "axios";

import { view_create_order_history } from "../import/ApiEndPoint/Api";
import { _Get } from "../import/ApiEndPoint/ApiCalling";

const injectOnce = () => {
  if (document.getElementById("gst-inline-css")) return;
  const s = document.createElement("style");
  s.id = "gst-inline-css";
  s.textContent = `
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .p-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .p-table{ width:100%; border-collapse:collapse; min-width:1200px; }
    .p-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:800; text-transform:uppercase;
      font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb;
      padding:10px; text-align:left; white-space:nowrap;
    }
    .p-row{ border-bottom:1px solid #f1f5f9; }
    .p-cell{ padding:10px; font-size:13px; color:#0f172a; vertical-align:middle; }
    .p-table tbody tr:hover{ background:#f9fbff; }
    .pill-muted{ color:#6b7280; font-size:12px; }
    .num{ text-align:right; font-variant-numeric: tabular-nums; }

    .badge-mode{
      display:inline-flex; align-items:center; gap:6px;
      padding:6px 10px; border-radius:999px; font-weight:900; font-size:12px;
      border:1px solid #e5e7eb; background:#fff; white-space:nowrap;
    }

    /* ===== Single-line header (like your new report headers) ===== */
    .p-headrow{
      display:flex;
      align-items:flex-end;
      gap:10px;
      flex-wrap:nowrap;            /* force one line */
      overflow-x:auto;             /* scroll if small */
      -webkit-overflow-scrolling: touch;
      padding-bottom:2px;
    }
    .p-headrow::-webkit-scrollbar{height:6px}
    .p-headrow::-webkit-scrollbar-thumb{background:#e5e7eb;border-radius:999px}

    .p-field{
      display:flex;
      flex-direction:column;
      gap:4px;
      min-width:170px;
    }
    .p-field.small{min-width:140px}
    .p-field.wide{min-width:240px}

    .p-lbl{
      font-size:11px;
      color:#64748b;
      font-weight:900;
      letter-spacing:.02em;
      line-height:1;
      margin:0;
      text-transform:uppercase;
      white-space:nowrap;
    }

    .p-ctl{
      height:32px !important;
      border-radius:12px !important;
      border:1px solid #e5e7eb !important;
      padding:6px 10px !important;
      background:#fff;
    }

    .p-btn{
      height:32px;
      border-radius:12px;
      padding:0 12px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      white-space:nowrap;
      font-weight:800;
    }

    .p-iconbtn{
      height:32px;
      width:42px;
      border-radius:12px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      padding:0;
      font-weight:900;
    }

    .p-spacer{ flex:1; min-width:16px; }

    .p-miniCount{
      padding-bottom:6px;
      font-weight:900;
      color:#0f172a;
      white-space:nowrap;
    }
  `;
  document.head.appendChild(s);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const fix2 = (val) => Number((Number(val) || 0).toFixed(2));

export default function GSTInputOutputReports() {
  const [loading, setLoading] = useState(false);

  const [finalRows, setFinalRows] = useState([]); // all combined
  const [filteredRows, setFilteredRows] = useState([]); // month/year filter
  const [searchRows, setSearchRows] = useState([]); // after search

  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [searchText, setSearchText] = useState("");

  const [viewMode, setViewMode] = useState("ALL"); // ALL | INPUT | OUTPUT

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const database = userData?.database;
  const userId = userData?._id;

  useEffect(() => injectOnce(), []);

  // ---------------- FETCH GST INPUT (PURCHASE) ----------------
  const fetchGSTInput = async () => {
    const arr = [];
    const res = await axios.get(
      `https://customer-node.rupioo.com/purchase-order/gst-input/${database}`,
    );
    const list = res?.data?.GstInput || [];
    const completed = list.filter(
      (it) => String(it?.status || "").toLowerCase() === "completed",
    );

    const companyDetails = JSON.parse(
      localStorage.getItem("Companydetail") || "{}",
    );
    const prefix = companyDetails?.Prefix || "";
    const suffix = companyDetails?.Suffix || "";

    completed.forEach((it) => {
      const cgst = Number(it?.cgstTotal ?? 0);
      const sgst = Number(it?.sgstTotal ?? 0);
      const igst = Number(it?.igstTotal ?? 0);
      const total = cgst + sgst + igst;

      const invoiceNo = it?.invoiceId
        ? `${prefix}${it.invoiceId}${suffix}`
        : "N/A";

      arr.push({
        partyName: it?.fullName || "N/A",
        invoiceNo: invoiceNo || "N/A",
        date: it?.date || null,
        invoiceValue: Number(it?.grandTotal || 0),
        taxableValue: Number(it?.amount || 0),

        cgstInput: cgst,
        sgstInput: sgst,
        igstInput: igst,
        totalInput: total,

        cgstOutput: 0,
        sgstOutput: 0,
        igstOutput: 0,
        totalOutput: 0,

        availableGST: total - 0,
      });
    });

    return arr;
  };

  // ---------------- FETCH GST OUTPUT (SALES) ----------------
  const fetchGSTOutput = async () => {
    const arr = [];
    const res = await _Get(view_create_order_history, userId, database);
    const list = res?.orderHistory || [];

    const companyDetails = JSON.parse(
      localStorage.getItem("Companydetail") || "{}",
    );
    const prefix = companyDetails?.Prefix || "";
    const suffix = companyDetails?.Suffix || "";

    const completed = list.filter(
      (it) => String(it?.status || "").toLowerCase() === "completed",
    );

    completed.forEach((it) => {
      const cgst = Number(it?.cgstTotal ?? 0);
      const sgst = Number(it?.sgstTotal ?? 0);
      const igst = Number(it?.igstTotal ?? 0);
      const total = cgst + sgst + igst;

      const invoiceNo = it?.invoiceId
        ? `${prefix}${it.invoiceId}${suffix}`
        : "N/A";

      arr.push({
        partyName: it?.fullName || "N/A",
        invoiceNo: invoiceNo || "N/A",
        date: it?.date || null,
        invoiceValue: Number(it?.grandTotal || 0),
        taxableValue: Number(it?.amount || 0),

        cgstInput: 0,
        sgstInput: 0,
        igstInput: 0,
        totalInput: 0,

        cgstOutput: cgst,
        sgstOutput: sgst,
        igstOutput: igst,
        totalOutput: total,

        availableGST: 0 - total,
      });
    });

    return arr;
  };

  // ---------------- LOAD BOTH ----------------
  const loadData = async () => {
    try {
      setLoading(true);

      const inputData = await fetchGSTInput();
      const outputData = await fetchGSTOutput();

      const final = [...inputData, ...outputData].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );

      setFinalRows(final);

      // default month/year = current
      const now = new Date();
      const cm = String(now.getMonth() + 1).padStart(2, "0");
      const cy = String(now.getFullYear());

      setMonth(cm);
      setYear(cy);

      const autoFiltered = final.filter((it) => {
        if (!it.date) return false;
        const d = new Date(it.date);
        return (
          String(d.getFullYear()) === cy &&
          String(d.getMonth() + 1).padStart(2, "0") === cm
        );
      });

      setFilteredRows(autoFiltered);
      setSearchRows(autoFiltered);
      setSearchText("");
      setViewMode("ALL");
      setPage(1);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- FILTER SUBMIT (Month/Year) ----------------
  const handleFilterSubmit = () => {
    let rows = finalRows;

    rows = rows.filter((it) => {
      if (!it.date) return false;
      const d = new Date(it.date);
      return (
        String(d.getFullYear()) === String(year) &&
        String(d.getMonth() + 1).padStart(2, "0") === String(month)
      );
    });

    setFilteredRows(rows);
    applySearch(searchText, rows);
    setPage(1);
  };

  // ---------------- SEARCH ----------------
  const applySearch = (text, sourceRows = null) => {
    const q = (text || "").trim().toLowerCase();
    const base = sourceRows === null ? filteredRows : sourceRows;

    if (!q) {
      setSearchRows(base);
      setSearchText(text);
      setPage(1);
      return;
    }

    const filtered = base.filter((r) => {
      const pn = (r.partyName || "").toString().toLowerCase();
      const inv = (r.invoiceNo || "").toString().toLowerCase();
      return pn.includes(q) || inv.includes(q);
    });

    setSearchRows(filtered);
    setSearchText(text);
    setPage(1);
  };

  // view mode rows
  const viewFilteredRows = useMemo(() => {
    const base = searchRows || [];
    if (viewMode === "INPUT")
      return base.filter((r) => Number(r.totalInput || 0) > 0);
    if (viewMode === "OUTPUT")
      return base.filter((r) => Number(r.totalOutput || 0) > 0);
    return base;
  }, [searchRows, viewMode]);

  // totals based on view mode
  const totals = useMemo(() => {
    return (viewFilteredRows || []).reduce(
      (acc, r) => {
        acc.invoiceValue += Number(r.invoiceValue || 0);
        acc.taxableValue += Number(r.taxableValue || 0);

        acc.cgstInput += Number(r.cgstInput || 0);
        acc.sgstInput += Number(r.sgstInput || 0);
        acc.igstInput += Number(r.igstInput || 0);
        acc.totalInput += Number(r.totalInput || 0);

        acc.cgstOutput += Number(r.cgstOutput || 0);
        acc.sgstOutput += Number(r.sgstOutput || 0);
        acc.igstOutput += Number(r.igstOutput || 0);
        acc.totalOutput += Number(r.totalOutput || 0);

        return acc;
      },
      {
        invoiceValue: 0,
        taxableValue: 0,
        cgstInput: 0,
        sgstInput: 0,
        igstInput: 0,
        totalInput: 0,
        cgstOutput: 0,
        sgstOutput: 0,
        igstOutput: 0,
        totalOutput: 0,
      },
    );
  }, [viewFilteredRows]);

  const availableGSTTotal = useMemo(() => {
    if (viewMode === "INPUT") return totals.totalInput;
    if (viewMode === "OUTPUT") return -totals.totalOutput;
    return totals.totalInput - totals.totalOutput;
  }, [totals, viewMode]);

  // ---------------- EXPORT PDF ----------------
  const exportPDF = () => {
    const doc = new jsPDF("landscape", "pt", "a4");
    const title = "GST Input / Output Report";

    const head = [
      [
        "S.No",
        "Party",
        "Invoice No",
        "Date",
        "Invoice Value",
        "Taxable Value",
        "CGST Input",
        "SGST Input",
        "IGST Input",
        "Total Input",
        "CGST Output",
        "SGST Output",
        "IGST Output",
        "Total Output",
        "Available GST",
      ],
    ];

    const body = (viewFilteredRows || []).map((r, idx) => [
      idx + 1,
      r.partyName,
      r.invoiceNo,
      r.date ? formatDate(r.date) : "-",
      fix2(r.invoiceValue),
      fix2(r.taxableValue),
      fix2(r.cgstInput),
      fix2(r.sgstInput),
      fix2(r.igstInput),
      fix2(r.totalInput),
      fix2(r.cgstOutput),
      fix2(r.sgstOutput),
      fix2(r.igstOutput),
      fix2(r.totalOutput),
      fix2(r.availableGST),
    ]);

    body.push([
      "",
      "TOTAL",
      "",
      "",
      fix2(totals.invoiceValue),
      fix2(totals.taxableValue),
      fix2(viewMode === "OUTPUT" ? 0 : totals.cgstInput),
      fix2(viewMode === "OUTPUT" ? 0 : totals.sgstInput),
      fix2(viewMode === "OUTPUT" ? 0 : totals.igstInput),
      fix2(viewMode === "OUTPUT" ? 0 : totals.totalInput),
      fix2(viewMode === "INPUT" ? 0 : totals.cgstOutput),
      fix2(viewMode === "INPUT" ? 0 : totals.sgstOutput),
      fix2(viewMode === "INPUT" ? 0 : totals.igstOutput),
      fix2(viewMode === "INPUT" ? 0 : totals.totalOutput),
      fix2(availableGSTTotal),
    ]);

    doc.setFontSize(14);
    doc.text(title, 40, 40);

    doc.autoTable({
      startY: 60,
      head,
      body,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: {
        fillColor: [244, 247, 246],
        textColor: 15,
        fontStyle: "bold",
      },
      columnStyles: {
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
        11: { halign: "right" },
        12: { halign: "right" },
        13: { halign: "right" },
        14: { halign: "right" },
      },
      margin: { left: 20, right: 20 },
      didDrawPage: function (data) {
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        doc.setFontSize(9);
        doc.text(
          `Page ${doc.internal.getNumberOfPages()}`,
          data.settings.margin.left,
          pageHeight - 10,
        );
      },
    });

    const fileName = `GST_Report_${year}_${month}_${viewMode}.pdf`;
    doc.save(fileName);
  };

  // ---------------- EXPORT EXCEL ----------------
  const exportExcel = () => {
    const dataToExport = (viewFilteredRows || []).map((r) => ({
      Party: r.partyName,
      "Invoice No": r.invoiceNo,
      Date: r.date ? formatDate(r.date) : "-",
      "Invoice Value": r.invoiceValue ?? 0,
      "Taxable Value": r.taxableValue ?? 0,
      "CGST Input": r.cgstInput ?? 0,
      "SGST Input": r.sgstInput ?? 0,
      "IGST Input": r.igstInput ?? 0,
      "Total Input": r.totalInput ?? 0,
      "CGST Output": r.cgstOutput ?? 0,
      "SGST Output": r.sgstOutput ?? 0,
      "IGST Output": r.igstOutput ?? 0,
      "Total Output": r.totalOutput ?? 0,
      "Available GST": r.availableGST ?? 0,
    }));

    dataToExport.push({
      Party: "TOTAL",
      "Invoice No": "",
      Date: "",
      "Invoice Value": fix2(totals.invoiceValue),
      "Taxable Value": fix2(totals.taxableValue),
      "CGST Input": fix2(viewMode === "OUTPUT" ? 0 : totals.cgstInput),
      "SGST Input": fix2(viewMode === "OUTPUT" ? 0 : totals.sgstInput),
      "IGST Input": fix2(viewMode === "OUTPUT" ? 0 : totals.igstInput),
      "Total Input": fix2(viewMode === "OUTPUT" ? 0 : totals.totalInput),
      "CGST Output": fix2(viewMode === "INPUT" ? 0 : totals.cgstOutput),
      "SGST Output": fix2(viewMode === "INPUT" ? 0 : totals.sgstOutput),
      "IGST Output": fix2(viewMode === "INPUT" ? 0 : totals.igstOutput),
      "Total Output": fix2(viewMode === "INPUT" ? 0 : totals.totalOutput),
      "Available GST": fix2(availableGSTTotal),
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "GST_Report");

    const fileName = `GST_Report_${year}_${month}_${viewMode}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // ---------------- PAGINATION ----------------
  const indexOfLast = page * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRows = viewFilteredRows.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(
    1,
    Math.ceil(viewFilteredRows.length / rowsPerPage),
  );

  const modeLabel =
    viewMode === "INPUT"
      ? "GST INPUT"
      : viewMode === "OUTPUT"
        ? "GST OUTPUT"
        : "ALL";

  return (
    <>
      {/* ===== Header (single line, no big title / no count pill) ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 10, background: "#f4f7f6" }}
          >
            <div className="p-headrow">
              <span className="badge-mode">{modeLabel}</span>

              {/* Mode Buttons */}
              <Button
                className="p-btn"
                color={viewMode === "INPUT" ? "primary" : "secondary"}
                onClick={() => {
                  setViewMode("INPUT");
                  setPage(1);
                }}
              >
                GST Input
              </Button>

              <Button
                className="p-btn"
                color={viewMode === "OUTPUT" ? "danger" : "secondary"}
                onClick={() => {
                  setViewMode("OUTPUT");
                  setPage(1);
                }}
              >
                GST Output
              </Button>

              <Button
                className="p-btn"
                color={viewMode === "ALL" ? "success" : "secondary"}
                onClick={() => {
                  setViewMode("ALL");
                  setPage(1);
                }}
              >
                All
              </Button>

              {/* Search */}
              <div className="p-field wide">
                <div className="p-lbl">Search</div>
                <Input
                  className="p-ctl"
                  placeholder="Party or Invoice…"
                  value={searchText}
                  onChange={(e) => applySearch(e.target.value)}
                />
              </div>

              {/* Year */}
              <div className="p-field small">
                <div className="p-lbl">Year</div>
                <Input
                  className="p-ctl"
                  type="select"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                >
                  {[0, 1, 2, 3, 4].map((n) => {
                    const y = String(new Date().getFullYear() - n);
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </Input>
              </div>

              {/* Month */}
              <div className="p-field small">
                <div className="p-lbl">Month</div>
                <Input
                  className="p-ctl"
                  type="select"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  <option value="01">Jan</option>
                  <option value="02">Feb</option>
                  <option value="03">Mar</option>
                  <option value="04">Apr</option>
                  <option value="05">May</option>
                  <option value="06">Jun</option>
                  <option value="07">Jul</option>
                  <option value="08">Aug</option>
                  <option value="09">Sep</option>
                  <option value="10">Oct</option>
                  <option value="11">Nov</option>
                  <option value="12">Dec</option>
                </Input>
              </div>

              <Button
                className="p-btn"
                color="primary"
                onClick={handleFilterSubmit}
                disabled={loading}
                title="Apply Month/Year"
              >
                Submit
              </Button>

              {/* Export icons (separate) */}
              <Button
                className="p-iconbtn"
                outline
                color="primary"
                onClick={exportPDF}
                disabled={loading}
                title="Download PDF"
              >
                PDF
              </Button>

              <Button
                className="p-iconbtn"
                outline
                color="primary"
                onClick={exportExcel}
                disabled={loading}
                title="Download Excel"
              >
                XLS
              </Button>

              <Button
                className="p-btn"
                color="secondary"
                onClick={loadData}
                disabled={loading}
                title="Reload"
              >
                {loading ? "Loading..." : "Reload"}
              </Button>

              <div className="p-spacer" />

              <div
                className="pill-muted"
                style={{ paddingBottom: 6, whiteSpace: "nowrap" }}
              >
                Default: current month/year
              </div>

              <div className="p-miniCount" title="Rows in current view">
                {viewFilteredRows.length}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table Card ===== */}
      <Row>
        <Col sm="12">
          <Card className="p-card" style={{ padding: 12 }}>
            <div className="p-table-wrap">
              <table className="p-table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>S.No</th>
                    <th style={{ minWidth: 220 }}>Party</th>
                    <th style={{ minWidth: 160 }}>Invoice No</th>
                    <th style={{ width: 140 }}>Date</th>
                    <th style={{ minWidth: 140 }}>Invoice Value</th>
                    <th style={{ minWidth: 140 }}>Taxable Value</th>
                    <th style={{ minWidth: 120 }}>CGST Input</th>
                    <th style={{ minWidth: 120 }}>SGST Input</th>
                    <th style={{ minWidth: 120 }}>IGST Input</th>
                    <th style={{ minWidth: 120 }}>Total Input</th>
                    <th style={{ minWidth: 120 }}>CGST Output</th>
                    <th style={{ minWidth: 120 }}>SGST Output</th>
                    <th style={{ minWidth: 120 }}>IGST Output</th>
                    <th style={{ minWidth: 120 }}>Total Output</th>
                    <th style={{ minWidth: 140 }}>Available GST</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr className="p-row">
                      <td
                        className="p-cell"
                        colSpan={15}
                        style={{ textAlign: "center" }}
                      >
                        <Spinner size="sm" /> Loading...
                      </td>
                    </tr>
                  ) : currentRows.length === 0 ? (
                    <tr className="p-row">
                      <td
                        className="p-cell"
                        colSpan={15}
                        style={{ textAlign: "center" }}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    currentRows.map((it, index) => (
                      <tr className="p-row" key={index}>
                        <td className="p-cell">{indexOfFirst + index + 1}</td>
                        <td className="p-cell">{it.partyName}</td>
                        <td className="p-cell">{it.invoiceNo}</td>
                        <td className="p-cell">{formatDate(it.date)}</td>

                        <td className="p-cell num">{fix2(it.invoiceValue)}</td>
                        <td className="p-cell num">{fix2(it.taxableValue)}</td>

                        <td className="p-cell num">{fix2(it.cgstInput)}</td>
                        <td className="p-cell num">{fix2(it.sgstInput)}</td>
                        <td className="p-cell num">{fix2(it.igstInput)}</td>
                        <td className="p-cell num">{fix2(it.totalInput)}</td>

                        <td className="p-cell num">{fix2(it.cgstOutput)}</td>
                        <td className="p-cell num">{fix2(it.sgstOutput)}</td>
                        <td className="p-cell num">{fix2(it.igstOutput)}</td>
                        <td className="p-cell num">{fix2(it.totalOutput)}</td>

                        <td
                          className="p-cell num"
                          style={{
                            color:
                              Number(it.availableGST) >= 0 ? "green" : "red",
                          }}
                        >
                          {fix2(it.availableGST)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                <tfoot style={{ background: "#f8fafc", fontWeight: 900 }}>
                  <tr>
                    <td
                      className="p-cell"
                      colSpan={4}
                      style={{ textAlign: "right" }}
                    >
                      TOTAL
                    </td>
                    <td className="p-cell num">{fix2(totals.invoiceValue)}</td>
                    <td className="p-cell num">{fix2(totals.taxableValue)}</td>

                    <td className="p-cell num">
                      {fix2(viewMode === "OUTPUT" ? 0 : totals.cgstInput)}
                    </td>
                    <td className="p-cell num">
                      {fix2(viewMode === "OUTPUT" ? 0 : totals.sgstInput)}
                    </td>
                    <td className="p-cell num">
                      {fix2(viewMode === "OUTPUT" ? 0 : totals.igstInput)}
                    </td>
                    <td className="p-cell num">
                      {fix2(viewMode === "OUTPUT" ? 0 : totals.totalInput)}
                    </td>

                    <td className="p-cell num">
                      {fix2(viewMode === "INPUT" ? 0 : totals.cgstOutput)}
                    </td>
                    <td className="p-cell num">
                      {fix2(viewMode === "INPUT" ? 0 : totals.sgstOutput)}
                    </td>
                    <td className="p-cell num">
                      {fix2(viewMode === "INPUT" ? 0 : totals.igstOutput)}
                    </td>
                    <td className="p-cell num">
                      {fix2(viewMode === "INPUT" ? 0 : totals.totalOutput)}
                    </td>

                    <td
                      className="p-cell num"
                      style={{
                        color: Number(availableGSTTotal) >= 0 ? "green" : "red",
                      }}
                    >
                      {fix2(availableGSTTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Pagination (same behavior as your current page) */}
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                color="secondary"
                disabled={page === 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>

              <span className="pill-muted" style={{ fontSize: 13 }}>
                Page <b>{page}</b> of <b>{totalPages}</b>
              </span>

              <Button
                color="secondary"
                disabled={page === totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>

              <div style={{ marginLeft: "auto" }} className="pill-muted">
                Showing <b>{currentRows.length}</b> of{" "}
                <b>{viewFilteredRows.length}</b> rows
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
